// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import axios from '../utils/axiosConfig';
import { useTheme } from '../context/ThemeContext';

const StargateUIViewer = ({ hostname, friendlyName, open, onClose }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  // The iframe src is built ASYNC now (2026-07-28 pentest, High: creds-in-URLs). We no longer put the
  // user JWT + device api_key in the URL; instead we mint a short-lived, ip-scoped, opaque view-token
  // and load the iframe with only `?ip=&vt=…`. Empty until the token is minted.
  const [iframeUrl, setIframeUrl] = useState('');

  // On open (and on Refresh, which bumps iframeKey), mint a fresh view-token then build the src.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setIframeUrl('');
    (async () => {
      try {
        // POST is Bearer-authenticated by the axios instance; the backend resolves the device
        // api_key SERVER-SIDE by IP (the reliable lookup), so neither the JWT nor the api_key travels
        // in a URL. serial_number is deliberately NOT sent — it would skip the by-IP resolution.
        const resp = await axios.post('/proxy/ui-view-token', null, { params: { ip: hostname } });
        const vt = resp?.data?.view_token;
        if (!vt) throw new Error('view-token mint returned no token');
        if (cancelled) return;
        const qp = new URLSearchParams();
        qp.append('ip', hostname);
        qp.append('vt', vt);
        setIframeUrl(`/api/proxy/ui/?${qp.toString()}`);
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, iframeKey, hostname]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(false);
    setIframeKey((prev) => prev + 1);
  };

  const handleOpenInNewTab = () => {
    window.open(`http://${hostname}`, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          height: '95vh',
          maxWidth: '1600px',
          m: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">{friendlyName || hostname} - Device UI</Typography>
          {/* The iframe URL carries a live (short-lived) view-token — do not render it in the UI
              chrome where it could be shoulder-surfed/screenshotted (2026-07-28 pentest). */}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleRefresh} disabled={loading} size="small" title="Refresh">
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleOpenInNewTab} size="small" title="Open in new tab">
            <OpenInNewIcon />
          </IconButton>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: 'relative', height: 'calc(100% - 64px)' }}>
        {loading && !error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)',
              zIndex: 10,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Connecting to {friendlyName || hostname}...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This may take a few seconds
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDarkMode ? 'background.paper' : '#f5f7fa',
              zIndex: 10,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: 500,
                p: 4,
                borderRadius: 2,
                bgcolor: isDarkMode ? '#2a2a2a' : 'white',
                boxShadow: 3,
              }}
            >
              <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Unable to Connect
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Could not establish a connection to the device at {hostname}
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" component="div">
                  This could be because:
                  <ul style={{ margin: '8px 0' }}>
                    <li>The device is offline or unreachable</li>
                    <li>The device's web interface is disabled</li>
                    <li>Network connectivity issues</li>
                    <li>Firewall or security settings blocking access</li>
                  </ul>
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenInNewTab}
                  startIcon={<OpenInNewIcon />}
                >
                  Open in Browser
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {iframeUrl && (
          <iframe
            key={iframeKey}
            src={iframeUrl}
            title={`${friendlyName || hostname} UI`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: error ? 'none' : 'block',
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            allow="clipboard-read; clipboard-write"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StargateUIViewer;
